import type {
  PayrollAdjustmentCategory,
  PayrollAdjustmentMonthRow,
} from '../../../utils/payrollAdjustmentCategories';

export const PAYROLL_ADJUSTMENT_TYPE_FILTER_OPTIONS = [
  { value: '__all', label: 'Bezüge & Abzüge' },
  { value: 'bezug', label: 'Nur Bezüge' },
  { value: 'abzug', label: 'Nur Abzüge' },
] as const;

export const PAYROLL_ADJUSTMENT_FILTER_DROPDOWN_CLASS =
  'inline-flex h-9 min-w-[170px] items-center justify-between gap-2 rounded-md border border-app-border bg-app-surface px-3 py-0 text-left text-sm font-normal text-app-text outline-none transition-colors hover:border-app-border-strong focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20';

export const PAYROLL_ADJUSTMENT_TOOLBAR_CONTROL_CLASS = 'h-9';

export type PayrollAdjustmentTableSortColumn =
  | 'employee'
  | 'category'
  | 'lohnart'
  | 'date'
  | 'note'
  | 'amount';

export type PayrollAdjustmentSortState = {
  key: PayrollAdjustmentTableSortColumn;
  dir: 'asc' | 'desc';
};

export function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function computePayrollAdjustmentSummary(rows: PayrollAdjustmentMonthRow[]) {
  let bezug = 0;
  let abzug = 0;
  for (const row of rows) {
    if (row.type === 'bezug') bezug += row.amount;
    else abzug += row.amount;
  }
  return { bezug, abzug, net: bezug - abzug, count: rows.length };
}

export function filterPayrollAdjustmentRows(
  rows: PayrollAdjustmentMonthRow[],
  options: {
    search: string;
    typeFilter: string;
    categoryByKey: Map<string, PayrollAdjustmentCategory>;
    employeeIds?: string[] | null;
  },
): PayrollAdjustmentMonthRow[] {
  const q = options.search.trim().toLowerCase();
  const selectedSet =
    options.employeeIds && options.employeeIds.length > 0
      ? new Set(options.employeeIds)
      : null;

  return rows.filter((row) => {
    if (selectedSet && !selectedSet.has(row.employee_id)) return false;
    if (options.typeFilter !== '__all' && row.type !== options.typeFilter) return false;
    if (!q) return true;
    const cat = options.categoryByKey.get(row.category_key);
    const hay = [
      row.employee_name,
      row.booking_date,
      cat?.label,
      cat?.lohnart,
      row.note,
      String(row.amount),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}

export function sortPayrollAdjustmentRows(
  rows: PayrollAdjustmentMonthRow[],
  sort: PayrollAdjustmentSortState,
  categoryByKey: Map<string, PayrollAdjustmentCategory>,
  fallbackDateDir: 'asc' | 'desc' = 'desc',
): PayrollAdjustmentMonthRow[] {
  const sorted = [...rows];
  const dirMul = sort.dir === 'asc' ? 1 : -1;
  sorted.sort((a, b) => {
    const catA = categoryByKey.get(a.category_key);
    const catB = categoryByKey.get(b.category_key);
    let cmp = 0;
    switch (sort.key) {
      case 'employee':
        cmp = a.employee_name.localeCompare(b.employee_name, 'de');
        break;
      case 'category':
        cmp = (catA?.label ?? a.category_key).localeCompare(catB?.label ?? b.category_key, 'de');
        break;
      case 'lohnart':
        cmp = (catA?.lohnart ?? '').localeCompare(catB?.lohnart ?? '', 'de');
        break;
      case 'date':
        cmp = a.booking_date.localeCompare(b.booking_date);
        break;
      case 'note':
        cmp = a.note.localeCompare(b.note, 'de');
        break;
      case 'amount':
        cmp = a.amount - b.amount;
        break;
      default:
        cmp = 0;
    }
    if (cmp !== 0) return cmp * dirMul;
    return fallbackDateDir === 'desc'
      ? b.booking_date.localeCompare(a.booking_date)
      : a.booking_date.localeCompare(b.booking_date);
  });
  return sorted;
}

export function togglePayrollAdjustmentSort(
  prev: PayrollAdjustmentSortState,
  key: PayrollAdjustmentTableSortColumn,
): PayrollAdjustmentSortState {
  return prev.key === key
    ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: 'desc' };
}
